import React from 'react';
import { Modal } from '../../../components/common/Modal';
import { formatCurrency, formatDecimal } from '../../../utils/format';
import type { PurchaseOrderExpenseOverview } from '../../../services/dashboardService';
import { useLanguage } from '../../../features/language/useLanguage';

interface ExpensesModalProps {
  isOpen: boolean;
  onClose: () => void;
  poExpenseOverview: PurchaseOrderExpenseOverview[];
  loading: boolean;
  isError: boolean;
  currencySymbol: string;
  onSupplierClick: (supplierId: string) => void;
}

export const ExpensesModal: React.FC<ExpensesModalProps> = ({
  isOpen,
  onClose,
  poExpenseOverview,
  loading,
  isError,
  currencySymbol,
  onSupplierClick,
}) => {
  const { t } = useLanguage();
  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={t('dashboard.expensesModal.title')}
      maxWidth="6xl"
    >
      <div className="space-y-4">
        {loading ? (
          <div className="py-10 text-center text-sm text-gray-500">{t('dashboard.expensesModal.loading')}</div>
        ) : isError ? (
          <div className="py-10 text-center text-sm text-red-600">{t('dashboard.expensesModal.error')}</div>
        ) : poExpenseOverview.length === 0 ? (
          <div className="py-10 text-center text-sm text-gray-500">
            {t('dashboard.expensesModal.empty')}
          </div>
        ) : (
          <div className="max-h-[70vh] overflow-y-auto pr-1">
            <div className="overflow-x-auto border border-gray-100 rounded-xl">
              <table className="min-w-full divide-y divide-gray-100">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">{t('dashboard.expensesModal.poNumber')}</th>
                    <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">{t('dashboard.expensesModal.buyWeight')}</th>
                    <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">{t('dashboard.expensesModal.arrivalWeight')}</th>
                    <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">{t('dashboard.expensesModal.yieldShrinkage')}</th>
                    <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">{t('dashboard.expensesModal.unitPrice')}</th>
                    <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">{t('dashboard.expensesModal.extraExpense')}</th>
                    <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">{t('dashboard.expensesModal.totalCost')}</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-100">
                  {poExpenseOverview.map((po) => (
                    <tr key={po.purchase_order_id} className="text-sm hover:bg-gray-50">
                      <td className="px-4 py-3">
                        <p className="font-semibold text-gray-900">{po.po_number}</p>
                        <p className="text-xs text-gray-500">
                          {po.supplier_id ? (
                            <button
                              type="button"
                              className="text-primary-600 hover:underline"
                              onClick={() => onSupplierClick(po.supplier_id!)}
                            >
                              {po.supplier_name || t('common.table.viewSupplier')}
                            </button>
                          ) : (
                            <span>{po.supplier_name || t('common.table.na')}</span>
                          )}
                          {' • '}
                          {po.order_date ? new Date(po.order_date).toLocaleDateString() : '-'}
                        </p>
                      </td>
                      <td className="px-4 py-3 text-right text-gray-700">{formatDecimal(po.buy_weight, 3)} kg</td>
                      <td className="px-4 py-3 text-right text-gray-700">{formatDecimal(po.arrival_weight, 3)} kg</td>
                      <td className="px-4 py-3 text-right text-gray-700">{formatDecimal(po.yield_shrinkage, 2)}%</td>
                      <td className="px-4 py-3 text-right text-gray-700">
                        {formatCurrency(po.buy_weight > 0 ? po.total_amount / po.buy_weight : 0, currencySymbol)}
                      </td>
                      <td className="px-4 py-3 text-right font-semibold text-rose-700">{formatCurrency(po.expense_total, currencySymbol)}</td>
                      <td className="px-4 py-3 text-right font-semibold text-gray-900">{formatCurrency(po.total_with_expenses, currencySymbol)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
};
